import { TrackingPoint } from '@/services/tracking-service';

export interface ElevationData {
  distance: number; // km
  altitude: number; // m
  timestamp: number;
}

export interface ElevationStats {
  totalGain: number; // metros
  totalLoss: number; // metros
  minAltitude: number;
  maxAltitude: number;
  currentAltitude: number;
}

/**
 * Calcula a distância entre dois pontos GPS usando a fórmula de Haversine
 */
export function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Raio da Terra em metros
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distância em metros
}

/**
 * Calcula distância acumulada para cada ponto
 */
export function calculateCumulativeDistance(
  points: TrackingPoint[]
): number[] {
  const distances: number[] = [0];
  let accumulated = 0;

  for (let i = 1; i < points.length; i++) {
    const dist = haversineDistance(
      points[i - 1].lat,
      points[i - 1].lng,
      points[i].lat,
      points[i].lng
    );
    accumulated += dist;
    distances.push(accumulated / 1000); // Converter para km
  }

  return distances;
}

/**
 * Suaviza dados de altitude usando média móvel
 */
export function smoothAltitudeData(
  altitudes: number[],
  windowSize: number = 3
): number[] {
  if (altitudes.length < windowSize) return altitudes;

  const smoothed: number[] = [];
  const halfWindow = Math.floor(windowSize / 2);

  for (let i = 0; i < altitudes.length; i++) {
    let sum = 0;
    let count = 0;

    for (let j = Math.max(0, i - halfWindow); j <= Math.min(altitudes.length - 1, i + halfWindow); j++) {
      sum += altitudes[j];
      count++;
    }

    smoothed.push(sum / count);
  }

  return smoothed;
}

/**
 * Calcula estatísticas de elevação
 */
export function calculateElevationStats(
  data: ElevationData[]
): ElevationStats {
  if (data.length === 0) {
    return {
      totalGain: 0,
      totalLoss: 0,
      minAltitude: 0,
      maxAltitude: 0,
      currentAltitude: 0,
    };
  }

  let totalGain = 0;
  let totalLoss = 0;
  let minAltitude = data[0].altitude;
  let maxAltitude = data[0].altitude;

  for (let i = 1; i < data.length; i++) {
    const diff = data[i].altitude - data[i - 1].altitude;
    
    if (diff > 0) {
      totalGain += diff;
    } else if (diff < 0) {
      totalLoss += Math.abs(diff);
    }

    minAltitude = Math.min(minAltitude, data[i].altitude);
    maxAltitude = Math.max(maxAltitude, data[i].altitude);
  }

  return {
    totalGain: Math.round(totalGain),
    totalLoss: Math.round(totalLoss),
    minAltitude: Math.round(minAltitude),
    maxAltitude: Math.round(maxAltitude),
    currentAltitude: Math.round(data[data.length - 1].altitude),
  };
}

/**
 * Formata altitude para exibição
 */
export function formatAltitude(meters: number): string {
  return `${Math.round(meters)}m`;
}

/**
 * Reduz o número de pontos mantendo a forma geral do perfil
 */
export function decimateData(
  data: ElevationData[],
  targetSize: number = 100
): ElevationData[] {
  if (data.length <= targetSize) return data;

  const step = Math.ceil(data.length / targetSize);
  const decimated: ElevationData[] = [];

  for (let i = 0; i < data.length; i += step) {
    decimated.push(data[i]);
  }

  // Sempre incluir o último ponto
  if (decimated[decimated.length - 1] !== data[data.length - 1]) {
    decimated.push(data[data.length - 1]);
  }

  return decimated;
}
